import { createFileRoute, Link, redirect, useNavigate, useRouterState } from "@tanstack/react-router";
import { btn } from "@/components/ds/styles";
import { useSuspenseQuery, queryOptions, useQueryClient, useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { LayoutGrid, CalendarClock, ClipboardList, FileText, FolderOpen, UserRound, LogOut, ListChecks, Baby } from "lucide-react";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { PhoneField, useDial, prefillPhone, phoneErrorMessage } from "@/components/common/phone-field";
import { toE164 } from "@/lib/dial-codes";
import { userErrorMessage } from "@/lib/user-error";
import { getMyProfile } from "@/lib/dr-sara.functions";
import { listMyBookings } from "@/lib/booking.functions";
import { listServices } from "@/lib/public.functions";
import {
  listMyCases,
  listMyReports,
  listMyAttempts,
  getAttempt,
  submitAttempt,
  updateProfile,
} from "@/lib/clinic.functions";
import { clinicAccessQuery } from "@/lib/clinic-access";
import { renderMarkdown } from "@/lib/markdown";
import { listMyActionPlans, getClientPlanDownloadUrl } from "@/lib/clinic/plan-ai.functions";
import {
  listCaseCheckpoints,
  submitCheckpointResponse,
  getDueCheckpoints,
  CHECKPOINT_SCALES,
  CHECKPOINT_FREQ,
} from "@/lib/clinic/checkpoints.functions";
import { CalendarCheck2, Clock, MapPin, MessageCircle } from "lucide-react";
import { AlertCircle, Upload } from "lucide-react";
import { listMyPendingForms } from "@/lib/forms.functions";
import logoWide from "@/assets/drsara-logo-maroon.png.asset.json";
import { listReportAttachments } from "@/lib/clinic.functions";
import { Paperclip } from "lucide-react";
import { RouteError, RouteNotFound } from "@/components/route-boundaries";
import { SECTIONS } from "@/data/bousala-v1.config";
import { getRepairableAttempt } from "@/lib/bousala.functions";
import { ChildFilePanel } from "@/components/portal/child-file-panel";
import { PublicShell } from "@/components/public/public-shell";
import { TabBar } from "@/components/ds/tab-bar";
import { DS_INPUT, Field } from "@/components/ds/field";
import { PortalOverview } from "@/components/portal/overview";
import { BookingCard } from "@/components/portal/booking-card";
import { publicSiteSettingsQuery } from "@/routes/_public/route";
import { toWhatsAppLink } from "@/lib/whatsapp-link";
import { SmartOrderCard } from "@/components/bousala/smart/smart-order-card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { getTraumaGate } from "@/lib/trauma/gate.functions";
import { TRAUMA_COURSES_UI } from "@/lib/trauma/ui-copy";


const profileQuery = () =>
  queryOptions({
    queryKey: ["me", "profile"],
    queryFn: () => getMyProfile(),
  });

const myBookingsQuery = () =>
  queryOptions({ queryKey: ["me", "bookings"], queryFn: () => listMyBookings() });

const servicesQuery = () =>
  queryOptions({ queryKey: ["public", "services"], queryFn: () => listServices() });

const searchSchema = z.object({
  tab: z.enum(["overview","cases","child_file","bookings","reports","tests","forms","profile"]).optional(),
});
type TabId = "overview" | "cases" | "child_file" | "bookings" | "reports" | "tests" | "forms" | "profile";

/**
 * Tabs that only make sense once the user has a clinic relationship
 * (a case, a booking, or a session). "تقاريري" lists case reports, not
 * Bousala reports, so it counts as clinic.
 */
const CLINIC_TABS: TabId[] = ["cases", "child_file", "bookings", "reports", "forms"];

export const Route = createFileRoute("/_authenticated/portal/")({
  validateSearch: searchSchema,
  beforeLoad: async ({ context, search }) => {
    const access = await context.queryClient.ensureQueryData(clinicAccessQuery());
    const tab = (search as { tab?: TabId }).tab;
    if (!access?.hasClinic && tab && CLINIC_TABS.includes(tab)) {
      throw redirect({ to: "/portal", search: { tab: "tests" as TabId }, replace: true });
    }
  },
  loader: ({ context }) => {
    context.queryClient.ensureQueryData(profileQuery());
  },
  head: () => ({
    meta: [
      { title: "حسابي — د. سارة النحّاس" },
      { name: "robots", content: "noindex" },
    ],
  }),
  notFoundComponent: RouteNotFound,
  errorComponent: RouteError,
  component: PortalPage,
});

const TABS: { id: TabId; label: string; icon: React.ComponentType<{ className?: string }> }[] = [
  { id: "overview", label: "نظرة عامة", icon: LayoutGrid },
  { id: "cases", label: "حالة طفلي", icon: FolderOpen },
  { id: "child_file", label: "ملف الطفل", icon: Baby },
  { id: "bookings", label: "حجوزاتي", icon: CalendarClock },
  { id: "reports", label: "تقاريري", icon: FileText },
  { id: "tests", label: "اختباراتي", icon: ClipboardList },
  { id: "forms", label: "نماذج معلّقة", icon: ListChecks },
  { id: "profile", label: "الإعدادات", icon: UserRound },
];

function useBousalaLangSearch(): { lang?: "ar" | "en" } {
  const [lang, setLang] = useState<"ar" | "en" | null>(null);
  useEffect(() => {
    try {
      const v = window.localStorage.getItem("bousala.lang");
      if (v === "ar" || v === "en") setLang(v);
    } catch {}
  }, []);
  return lang ? { lang } : {};
}

function PortalPage() {
  const { data: profile } = useSuspenseQuery(profileQuery());
  const { data: access } = useQuery(clinicAccessQuery());
  const hasClinic = access?.hasClinic ?? false;
  const tabs = hasClinic ? TABS : TABS.filter((t) => !CLINIC_TABS.includes(t.id));
  const search = useRouterState({ select: (s) => s.location.search }) as { tab?: TabId };
  const active: TabId = search.tab ?? "overview";
  const navigate = useNavigate();
  const { data: gate } = useQuery({ queryKey: ["trauma", "gate"], queryFn: () => getTraumaGate(), staleTime: 60_000, retry: false });

  return (
    <PublicShell ground="bg-cream-200">
      <div dir="rtl" className="mx-auto w-[min(100%-2.5rem,60rem)] py-8 lg:py-12">
        <div className="no-scrollbar -mx-5 overflow-x-auto px-5">
          <TabBar
            label="أقسام لوحتي"
            value={active}
            onChange={(v) => navigate({ to: "/portal", search: { tab: v === "overview" ? undefined : v } as any })}
            options={tabs.map((t) => ({ value: t.id, label: t.label }))}
            variant="pills"
          />
        </div>
        {gate?.canView && (
          <Link to="/portal/courses" className="mt-3 inline-flex min-h-11 items-center font-display text-[15px] font-bold text-amber-800">
            {TRAUMA_COURSES_UI.title} ←
          </Link>
        )}
        <section className="mt-8 min-w-0">
          {active === "overview" ? (
            <>
              <PortalOverview fullName={profile?.full_name} />
              <PortalLinks hasClinic={hasClinic} />
            </>
          ) : (
            <div>
              <TabHeader tab={active} />
              {active === "cases" && <MyCases />}
              {active === "child_file" && <ChildFilePanel />}
              {active === "bookings" && <MyBookings />}
              {active === "reports" && <MyReports />}
              {active === "tests" && <MyAttempts />}
              {active === "forms" && <MyPendingForms />}
              {active === "profile" && <ProfileEditor profile={profile} />}
            </div>
          )}
        </section>
      </div>
    </PublicShell>
  );
}

/** Compact links to every other portal area, so nothing is lost from the overview. */
function PortalLinks({ hasClinic }: { hasClinic: boolean }) {
  const items: { tab: TabId; label: string }[] = [
    ...(hasClinic ? [{ tab: "cases" as TabId, label: "حالة طفلي" }, { tab: "child_file" as TabId, label: "ملف الطفل" }] : []),
    ...(hasClinic ? [{ tab: "reports" as TabId, label: "تقاريري" }, { tab: "forms" as TabId, label: "نماذج معلّقة" }] : []),
    { tab: "tests", label: "اختباراتي" },
    { tab: "profile", label: "الإعدادات" },
  ];
  return (
    <nav aria-label="أقسام أخرى" className="mt-11 grid grid-cols-2 gap-3 lg:grid-cols-3">
      {items.map((i) => (
        <Link
          key={i.tab}
          to="/portal"
          search={{ tab: i.tab } as any}
          className="flex min-h-14 items-center justify-between rounded-ds-md border border-hairline-default bg-surface px-5 font-display text-[15px] font-bold text-brown-700 hover:bg-cream-50"
        >
          {i.label}
          <span aria-hidden>←</span>
        </Link>
      ))}
    </nav>
  );
}

const TAB_EYEBROW: Partial<Record<TabId, string>> = {
  cases: "متابعة العيادة", child_file: "متابعة العيادة", bookings: "الحجوزات", reports: "المستندات",
  tests: "الاختبارات", forms: "النماذج", profile: "الحساب",
};

function TabHeader({ tab }: { tab: TabId }) {
  const t = TABS.find((x) => x.id === tab)!;
  return (
    <div className="mb-6">
      {TAB_EYEBROW[tab] && <p className="font-display text-[14px] font-bold text-amber-800">{TAB_EYEBROW[tab]}</p>}
      <h2 className="mt-1 font-display text-[26px] font-extrabold text-brown-700">{t.label}</h2>
    </div>
  );
}

const primaryPill =
  `${btn.primary} disabled:opacity-60`;
const secondaryPill =
  "inline-flex min-h-12 items-center justify-center gap-2 rounded-ds-pill border border-hairline-default bg-surface px-5 font-display text-[15px] font-bold text-brown-700 transition-colors duration-ds-fast hover:bg-cream-50 disabled:opacity-60";
const ghostPill =
  "inline-flex min-h-12 items-center justify-center gap-2 rounded-ds-pill px-5 font-display text-[15px] font-bold text-brown-700 transition-colors duration-ds-fast hover:bg-cream-100 disabled:opacity-60";
const rowCls = "border-b border-hairline-subtle py-4 last:border-b-0";
const pillCls = "inline-flex items-center rounded-ds-pill px-3 py-1 font-display text-[13px] font-bold";
const latnDate = (d: string, o: Intl.DateTimeFormatOptions = { day: "numeric", month: "long", year: "numeric" }) =>
  new Intl.DateTimeFormat("ar-u-nu-latn", o).format(new Date(d));

/** Empty tab: an invitation on a quiet row plus one action row. */
function EmptyState({ message, cta }: { message: string; cta?: { to: "/book" | "/bousala"; label: string } }) {
  return (
    <div className="border-y border-hairline-subtle">
      <p className="py-5 text-[17px] leading-[1.85] text-brown-500">{message}</p>
      {cta && (
        <Link to={cta.to} className="flex min-h-14 items-center justify-between border-t border-hairline-subtle font-display text-[17px] font-bold text-amber-800 hover:text-brown-700">
          {cta.label}
        </Link>
      )}
    </div>
  );
}

/** Document row: 52px blue tile, title/meta, secondary «تنزيل» action. */
function DocRow({ title, meta, action }: { title: string; meta?: string; action: React.ReactNode }) {
  return (
    <li className="flex items-center gap-4 rounded-ds-md border border-hairline-default bg-surface p-4">
      <span className="grid size-[52px] shrink-0 place-items-center rounded-ds-md bg-blue-100 text-blue-700">
        <FileText className="size-6" strokeWidth={1.75} aria-hidden />
      </span>
      <div className="min-w-0 flex-1">
        <p className="font-display text-[17px] font-bold text-brown-700">{title}</p>
        {meta && <p className="mt-0.5 text-[14px] text-brown-500">{meta}</p>}
      </div>
      {action}
    </li>
  );
}

function MyPendingForms() {
  const { data, isLoading } = useQuery({
    queryKey: ["me", "pending-forms"],
    queryFn: () => listMyPendingForms(),
  });
  if (isLoading) return <div className="h-24 rounded-ds-lg bg-cream-100" aria-hidden />;
  const rows = data ?? [];
  if (!rows.length) {
    return <EmptyState message="ما في نماذج بانتظارك هلّق. أي نموذج جديد بيظهر هون." />;
  }
  return (
    <ul className="border-y border-hairline-subtle">
      {rows.map((f: any) => (
        <li key={f.id} className={`flex items-center justify-between gap-4 ${rowCls}`}>
          <div>
            <p className="font-display text-[17px] font-bold text-brown-700">{f.title_ar}</p>
            {f.category && <p className="mt-0.5 text-[14px] text-brown-500">{f.category}</p>}
          </div>
          <Link
            to="/forms/$slug"
            params={{ slug: f.slug }}
            className={primaryPill}
          >
            بدء التعبئة ←
          </Link>
        </li>
      ))}
    </ul>
  );
}

function ProfileEditor({ profile }: { profile: any }) {
  const prefill = prefillPhone(profile?.phone_whatsapp, profile?.phone_whatsapp_legacy);
  const [form, setForm] = useState({
    full_name: profile?.full_name ?? "",
    country: profile?.country ?? "",
    city: profile?.city ?? "",
  });
  const [phoneCountry, setPhoneCountry] = useState(prefill.code);
  const [phoneNational, setPhoneNational] = useState(prefill.national);
  const dial = useDial(phoneCountry);
  const [busy, setBusy] = useState(false);
  const fn = useServerFn(updateProfile);
  const qc = useQueryClient();

  async function save() {
    const e164 = toE164(dial, phoneNational);
    if (!e164) {
      toast.error(phoneErrorMessage("ar"));
      return;
    }
    setBusy(true);
    try {
      await fn({ data: { ...form, phone_whatsapp: e164 } });
      toast.success("تم حفظ ملفك");
      qc.invalidateQueries({ queryKey: ["me", "profile"] });
    } catch (e: any) { toast.error(userErrorMessage(e, "ar", "تعذّر حفظ الملف. منجرّب كمان مرة؟")); } finally { setBusy(false); }
  }
  return (
    <div className="grid gap-5 md:grid-cols-2">
      <Field id="profile-name" label="الاسم الكامل">
        <input id="profile-name" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className={DS_INPUT} />
      </Field>
      <PhoneField
        id="profile-phone"
        label="واتساب"
        required
        country={phoneCountry}
        national={phoneNational}
        onCountryChange={setPhoneCountry}
        onNationalChange={setPhoneNational}
      />
      <Field id="profile-country" label="الدولة">
        <input id="profile-country" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} className={DS_INPUT} />
      </Field>
      <Field id="profile-city" label="المدينة">
        <input id="profile-city" value={form.city ?? ""} onChange={(e) => setForm({ ...form, city: e.target.value })} className={DS_INPUT} />
      </Field>
      <div className="md:col-span-2 flex justify-end">
        <button onClick={save} disabled={busy} className={primaryPill}>
          {busy ? "جاري الحفظ…" : "حفظ التعديلات"}
        </button>
      </div>
    </div>
  );
}

function MyCases() {
  const { data, isLoading } = useQuery({ queryKey: ["me", "cases"], queryFn: () => listMyCases() });
  if (isLoading) return <div className="h-32 rounded-ds-lg bg-cream-100" aria-hidden />;
  if (!data || data.length === 0) {
    return <EmptyState message="لسّا ما في حالة مفتوحة. الحالة بتنفتح مع تأكيد أول جلسة." cta={{ to: "/book", label: "حجز استشارة ←" }} />;
  }
  return (
    <ul className="border-y border-hairline-subtle">
      {data.map((c: any) => {
        const upcoming = (c.sessions ?? []).find((s: any) => s.status === "scheduled" && s.scheduled_at && new Date(s.scheduled_at) > new Date());
        const lastReport = (c.reports ?? [])[0];
        return (
          <li key={c.id} className="border-b border-hairline-subtle py-6 last:border-b-0">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="font-display text-[17px] font-bold text-brown-700">{c.child_alias}</p>
                <p className="mt-0.5 text-[14px] text-brown-500">{c.child_age_band ?? "—"}</p>
              </div>
              <span className={`${pillCls} ${c.status === "active" ? "bg-green-100 text-green-800" : c.status === "paused" ? "bg-amber-100 text-amber-800" : "bg-cream-100 text-brown-500"}`}>{c.status === "active" ? "نشطة" : c.status === "paused" ? "موقوفة" : "مغلقة"}</span>
            </div>
            {upcoming && (
              <div className="mt-3 space-y-1 text-[15px] text-brown-700">
                <p className="flex items-center gap-2">
                  <CalendarCheck2 className="h-4 w-4" /> الجلسة القادمة:{" "}
                  {new Intl.DateTimeFormat("ar-u-nu-latn", {
                    day: "numeric",
                    month: "long",
                    year: "numeric",
                    hour: "numeric",
                    minute: "2-digit",
                    hour12: true,
                  }).format(new Date(upcoming.scheduled_at))}
                </p>
                {upcoming.session_mode === "in_person" && upcoming.location_note && (
                  <p className="flex items-start gap-2 text-[14px] text-brown-500">
                    <MapPin className="mt-0.5 h-3.5 w-3.5 flex-none" />
                    <span>{upcoming.location_note}</span>
                  </p>
                )}
                {upcoming.session_mode !== "in_person" && upcoming.meeting_link && (
                  <a
                    href={upcoming.meeting_link}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex min-h-11 items-center gap-1 text-[14px] font-bold text-amber-800 underline underline-offset-4"
                  >
                    رابط الجلسة
                  </a>
                )}
              </div>
            )}
            {lastReport && (
              <p className="mt-2 text-[15px] text-brown-700">آخر تقرير مشارك: <b>{lastReport.title_ar}</b></p>
            )}
            <p className="mt-2 text-[14px] text-brown-500">{c.sessions?.length ?? 0} جلسة · {c.reports?.length ?? 0} تقرير</p>
            <CheckpointsForCase caseId={c.id} />
          </li>
        );
      })}
    </ul>
  );
}

const SCALE_LABEL: Record<string, string> = {
  improved_a_lot: "تحسن كثيرًا",
  improved_a_bit: "تحسن قليلًا",
  no_change: "لم يتغير",
  worse: "ازداد",
};
const FREQ_LABEL: Record<string, string> = {
  daily: "يوميًا",
  most_days: "معظم الأيام",
  sometimes: "أحيانًا",
  rarely: "نادرًا",
};
const KIND_LABEL: Record<string, string> = { "30d": "متابعة 30 يوم", "90d": "متابعة 90 يوم" };

function CheckpointsForCase({ caseId }: { caseId: string }) {
  const qc = useQueryClient();
  // Trigger due-notification side-effect once per portal load (idempotent server-side).
  const dueFn = useServerFn(getDueCheckpoints);
  useEffect(() => {
    dueFn({ data: { scope: "mine" } }).catch(() => {});
  }, []);
  const listFn = useServerFn(listCaseCheckpoints);
  const { data } = useQuery({
    queryKey: ["me", "case-checkpoints", caseId],
    queryFn: () => listFn({ data: { caseId } }),
  });
  const items: any[] = data?.items ?? [];
  if (!items.length) return null;
  return (
    <div className="mt-4 space-y-3">
      {items.map((cp) => (
        <CheckpointCard
          key={cp.id}
          cp={cp}
          onDone={() => qc.invalidateQueries({ queryKey: ["me", "case-checkpoints", caseId] })}
        />
      ))}
    </div>
  );
}

function CheckpointCard({ cp, onDone }: { cp: any; onDone: () => void }) {
  const submitFn = useServerFn(submitCheckpointResponse);
  const [scales, setScales] = useState<Record<string, string>>(
    () => (cp.responses?.items ?? []).reduce((acc: any, it: any) => ({ ...acc, [it.indicator]: it.scale }), {}),
  );
  const [notes, setNotes] = useState<Record<string, string>>(
    () => (cp.responses?.items ?? []).reduce((acc: any, it: any) => ({ ...acc, [it.indicator]: it.note ?? "" }), {}),
  );
  const [stuckNote, setStuckNote] = useState<string>(cp.responses?.stuck_note ?? "");
  const [frequency, setFrequency] = useState<string>(cp.responses?.frequency ?? "");
  const [saving, setSaving] = useState(false);
  const completed = cp.status === "completed";
  const dueLabel = new Intl.DateTimeFormat("ar-u-nu-latn", { day: "numeric", month: "long", year: "numeric" }).format(new Date(cp.due_at));
  const indicators: any[] = cp.indicators ?? [];

  if (completed) {
    return (
      <div className="rounded-ds-lg bg-green-100 p-4">
        <p className="font-display text-[15px] font-bold text-green-800">{KIND_LABEL[cp.kind]} — {cp.report_title}</p>
        <p className="mt-1 text-[14px] text-brown-700">شكراً، وصلنا التحديث.</p>
        <ul className="mt-2 space-y-1 text-[14px] text-brown-700">
          {(cp.responses?.items ?? []).map((it: any) => (
            <li key={it.indicator}>{it.indicator}: <b>{SCALE_LABEL[it.scale] ?? it.scale}</b></li>
          ))}
        </ul>
      </div>
    );
  }

  async function onSubmit() {
    const items = indicators
      .filter((i) => scales[i.indicator])
      .map((i) => ({ indicator: i.indicator, scale: scales[i.indicator] as any, note: notes[i.indicator] || undefined }));
    if (items.length !== indicators.length) {
      toast.error("ناقص تقييم كل المؤشرات.");
      return;
    }
    if (!frequency) {
      toast.error("ناقص اختيار كم مرة طبّقتوا الأنشطة.");
      return;
    }
    setSaving(true);
    try {
      await submitFn({
        data: {
          checkpointId: cp.id,
          responses: { items, stuck_note: stuckNote, frequency: frequency as any },
        },
      });
      toast.success("شكراً، وصلنا التحديث.");
      onDone();
    } catch (e: any) {
      toast.error(e?.message ?? "تعذّر الحفظ.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="rounded-ds-xl border border-hairline-default bg-surface p-5" id={`checkpoint-${cp.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="font-display text-[15px] font-bold">{KIND_LABEL[cp.kind]} — {cp.report_title}</p>
          <p className="mt-0.5 text-[14px] text-brown-500">موعد التعبئة: {dueLabel}</p>
        </div>
        <span className={`${pillCls} bg-amber-100 text-amber-800`}>جاهزة للتعبئة</span>
      </div>
      <p className="mt-3 text-[15px] text-brown-500">مرّ شهر على الخطة. كيف صارت الأمور؟</p>
      <ul className="mt-3 space-y-3">
        {indicators.map((i) => (
          <li key={i.indicator} className="border-b border-hairline-subtle pb-4 last:border-b-0">
            <p className="font-display text-[15px] font-bold">{i.indicator}</p>
            {i.before && <p className="text-[14px] text-brown-500">قبل الخطة: {i.before}</p>}
            {i.target && <p className="text-[14px] text-brown-500">الهدف: {i.target}</p>}
            <div className="mt-2 grid grid-cols-2 gap-2 md:grid-cols-4">
              {CHECKPOINT_SCALES.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setScales((v) => ({ ...v, [i.indicator]: s }))}
                  aria-pressed={scales[i.indicator] === s}
                  className={`min-h-11 rounded-ds-pill border px-4 font-display text-[14px] font-bold transition-colors duration-ds-fast ${scales[i.indicator] === s ? "border-amber-600 bg-amber-100 text-brown-700" : "border-hairline-default bg-surface text-brown-700 hover:bg-cream-50"}`}
                >
                  {SCALE_LABEL[s]}
                </button>
              ))}
            </div>
            <input
              type="text"
              value={notes[i.indicator] ?? ""}
              onChange={(e) => setNotes((v) => ({ ...v, [i.indicator]: e.target.value }))}
              placeholder="ملاحظة اختيارية"
              className={`mt-2 ${DS_INPUT}`}
            />
          </li>
        ))}
      </ul>
      <div className="mt-4">
        <p className="font-display text-[15px] font-bold">كم مرة طبّقتوا الأنشطة؟</p>
        <div className="mt-2 flex flex-wrap gap-2">
          {CHECKPOINT_FREQ.map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => setFrequency(f)}
              aria-pressed={frequency === f}
              className={`min-h-11 rounded-ds-pill border px-4 font-display text-[14px] font-bold transition-colors duration-ds-fast ${frequency === f ? "border-amber-600 bg-amber-100 text-brown-700" : "border-hairline-default bg-surface text-brown-700 hover:bg-cream-50"}`}
            >
              {FREQ_LABEL[f]}
            </button>
          ))}
        </div>
      </div>
      <div className="mt-4">
        <p className="font-display text-[15px] font-bold">شو اللي لسّا صعب؟</p>
        <textarea
          value={stuckNote}
          onChange={(e) => setStuckNote(e.target.value)}
          rows={3}
          className={`mt-1 ${DS_INPUT}`}
        />
      </div>
      <button
        onClick={onSubmit}
        disabled={saving}
        className={`mt-4 ${primaryPill}`}
      >
        {saving ? "جاري الإرسال…" : "إرسال التحديث"}
      </button>
    </div>
  );
}

function MyReports() {
  const { data, isLoading } = useQuery({ queryKey: ["me", "reports"], queryFn: () => listMyReports() });
  const { data: plans } = useQuery({ queryKey: ["me", "action-plans"], queryFn: () => listMyActionPlans() });
  const [open, setOpen] = useState<any>(null);
  const downloadFn = useServerFn(getClientPlanDownloadUrl);
  const [downloading, setDownloading] = useState<string | null>(null);

  async function download(id: string) {
    setDownloading(id);
    try {
      const { url } = await downloadFn({ data: { id } });
      window.open(url, "_blank", "noopener");
    } catch (e: any) {
      toast.error(e?.message ?? "تعذّر تنزيل الخطة");
    } finally {
      setDownloading(null);
    }
  }

  if (isLoading) return <div className="h-24 rounded-ds-lg bg-cream-100" aria-hidden />;
  const hasPlans = (plans ?? []).length > 0;
  const hasReports = (data ?? []).length > 0;
  if (!hasPlans && !hasReports) return <EmptyState message="لسّا ما في تقارير مشاركة. التقارير بتظهر هون بعد الجلسات." cta={{ to: "/book", label: "حجز استشارة ←" }} />;
  return (
    <>
      {hasPlans && (
        <section className="mb-8">
          <span className={`${pillCls} bg-green-100 text-green-800`}>خطة العمل جاهزة</span>
          <ul className="mt-3 space-y-2.5">
            {(plans ?? []).map((p: any) => (
              <DocRow
                key={p.id}
                title={p.title_ar}
                meta={`${p.child_alias ?? ""} · ${latnDate(p.shared_at, { dateStyle: "medium" })}`}
                action={
                  <button onClick={() => download(p.id)} disabled={downloading === p.id} className={secondaryPill}>
                    {downloading === p.id ? "جاري التحضير…" : "تنزيل"}
                  </button>
                }
              />
            ))}
          </ul>
        </section>
      )}
      {hasReports && (
      <ul className="border-y border-hairline-subtle">
        {(data ?? []).map((r: any) => (
          <li key={r.id} className="border-b border-hairline-subtle last:border-b-0">
            <button onClick={() => setOpen(r)} className="flex min-h-14 w-full items-center gap-4 py-4 text-start transition-colors duration-ds-fast hover:bg-cream-100">
              <span className="grid size-[52px] shrink-0 place-items-center rounded-ds-md bg-blue-100 text-blue-700"><FileText className="size-6" strokeWidth={1.75} aria-hidden /></span>
              <div className="min-w-0 flex-1">
                <p className="font-display text-[17px] font-bold text-brown-700">{r.title_ar}</p>
                <p className="mt-0.5 text-[14px] text-brown-500">{r.case?.child_alias} · {latnDate(r.shared_at)}</p>
              </div>
              <span className="font-display text-[15px] font-bold text-amber-800">قراءة ←</span>
            </button>
          </li>
        ))}
      </ul>
      )}
      <Dialog open={Boolean(open)} onOpenChange={(v) => !v && setOpen(null)}>
        <DialogContent className="max-h-[85vh] w-full max-w-2xl overflow-y-auto rounded-ds-xl bg-cream-50 p-6" dir="rtl">
          {open && (
            <>
              <DialogHeader>
                <DialogTitle className="mb-4 font-display text-xl font-extrabold text-start">{open.title_ar}</DialogTitle>
              </DialogHeader>
              <div className="prose-ar" dangerouslySetInnerHTML={{ __html: renderMarkdown(open.body_md || "") }} />
              <ReportAttachmentsList reportId={open.id} />
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

function ReportAttachmentsList({ reportId }: { reportId: string }) {
  const { data } = useQuery({
    queryKey: ["report-attachments", reportId],
    queryFn: () => listReportAttachments({ data: { report_id: reportId } }),
  });
  if (!data || data.length === 0) return null;
  return (
    <div className="mt-6 border-t border-hairline-subtle pt-4">
      <p className="mb-3 font-display text-[15px] font-bold text-brown-700">المرفقات</p>
      <ul className="space-y-2">
        {data.map((a: any) => (
          <li key={a.id}>
            <a href={a.url} target="_blank" rel="noreferrer" className="inline-flex min-h-11 items-center gap-2 rounded-ds-pill border border-hairline-default bg-surface px-4 font-display text-[14px] font-bold text-brown-700 hover:bg-cream-50">
              <Paperclip className="size-4" strokeWidth={1.75} />
              {a.file_name}
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}

function MyAttempts() {
  const { data, isLoading } = useQuery({ queryKey: ["me", "attempts"], queryFn: () => listMyAttempts() });
  const [openId, setOpenId] = useState<string | null>(null);
  const bousalaLangSearch = useBousalaLangSearch();
  if (isLoading) return <div className="h-24 rounded-ds-lg bg-cream-100" aria-hidden />;
  if (!data || data.length === 0)
    return (
      <>
        <SmartOrderCard />
        <BousalaRepairBanner />
        <EmptyState message="لسّا ما في اختبارات بحسابك. البوصلة مجانية وبتاخد دقائق." cta={{ to: "/bousala", label: "بدء البوصلة ←" }} />
      </>
    );
  const tierLabel = (t?: string) => (t === "premium" ? "بريميوم" : t === "full" ? "الكامل" : "المجاني");
  const tierClass = (t?: string) =>
    t === "premium"
      ? "bg-amber-100 text-amber-800"
      : t === "full"
      ? "bg-green-100 text-green-800"
      : "bg-cream-100 text-brown-500";
  const bousalaTotal = SECTIONS.reduce((n, s) => n + s.items.length, 0);
  return (
    <>
      <SmartOrderCard />
      <BousalaRepairBanner />
      <ul className="border-y border-hairline-subtle">
        {data.map((a: any) => {
          const isBousala = a.instrument?.slug === "bousala-v1";
          const isDone = a.status === "scored" || a.status === "report_ready";
          const answered = isBousala
            ? Object.keys((a.answers ?? {}) as Record<string, unknown>).length
            : 0;
          const pct = isBousala && bousalaTotal > 0 ? Math.round((answered / bousalaTotal) * 100) : 0;
          return (
          <li key={a.id} className={rowCls}>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <p className="font-display text-[17px] font-bold text-brown-700">{a.instrument?.title_ar ?? "تقييم"}</p>
                <p className="mt-0.5 text-[14px] text-brown-500">{a.instrument?.subtitle_ar ?? ""}</p>
                {a.completed_at && (
                  <p className="mt-1 text-[14px] text-brown-500">
                    اكتمل: {latnDate(a.completed_at)}
                  </p>
                )}
                {isBousala && !isDone && answered > 0 && (
                  <p className="mt-1 text-[14px] font-bold text-amber-800">
                    تقدّمك: {pct}% · {answered} من {bousalaTotal}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2">
                {isBousala && (
                  <span className={`${pillCls} ${tierClass(a.tier)}`}>
                    {tierLabel(a.tier)}
                  </span>
                )}
                {isDone ? (
                  isBousala ? (
                    <Link
                      to="/portal/bousala/$attemptId"
                      params={{ attemptId: a.id }}
                      className={primaryPill}
                    >
                      فتح التقرير ←
                    </Link>
                  ) : (
                    <span className={`${pillCls} bg-green-100 text-green-800`}>
                      مكتمل · {a.scores?.total ?? 0}
                    </span>
                  )
                ) : isBousala ? (
                  <Link
                    to="/bousala/play"
                    search={bousalaLangSearch as any}
                    className={primaryPill}
                  >
                    {answered > 0 ? "متابعة الاختبار ←" : "بدء الاختبار ←"}
                  </Link>
                ) : (
                  <button
                    onClick={() => setOpenId(a.id)}
                    className={primaryPill}
                  >
                    بدء الاختبار ←
                  </button>
                )}
              </div>
            </div>
          </li>
          );
        })}
      </ul>
      {openId && <AttemptRunner id={openId} onClose={() => setOpenId(null)} />}
    </>
  );
}

/**
 * Shown only when this user has an attempt whose report is waiting on answers
 * that were lost on our side. The count makes the remaining effort visible.
 */
function BousalaRepairBanner() {
  const fn = useServerFn(getRepairableAttempt);
  const { data } = useQuery({
    queryKey: ["bousala", "repairable"],
    queryFn: () => fn(),
    refetchOnWindowFocus: false,
  });
  if (!data || data.missingIds.length === 0) return null;
  const n = data.missingIds.length;
  return (
    <div className="mb-6 rounded-ds-lg bg-amber-100 p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="font-display text-[17px] font-bold text-amber-800">
            تقريرك بانتظار إجابات قليلة
          </p>
          <p className="mt-1 text-[15px] leading-relaxed text-brown-700">
            بعض إجاباتك لم تصلنا بسبب خلل من طرفنا. {n === 1 ? "بقي سؤال واحد فقط" : `بقيت ${n} أسئلة فقط`} ويكتمل تقريرك.
          </p>
        </div>
        <Link
          to="/bousala/repair"
          className={`shrink-0 ${primaryPill}`}
        >
          إكمال الإجابات ←
        </Link>
      </div>
    </div>
  );
}

function AttemptRunner({ id, onClose }: { id: string; onClose: () => void }) {
  const { data, isLoading } = useQuery({ queryKey: ["attempt", id], queryFn: () => getAttempt({ data: { id } }) });
  const submit = useServerFn(submitAttempt);
  const qc = useQueryClient();
  const [answers, setAnswers] = useState<Record<string, number>>({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (data?.attempt?.answers) setAnswers(data.attempt.answers as any);
  }, [data]);

  const instrument = data?.instrument;
  const sections = (instrument?.config as any)?.sections ?? [];

  async function finish() {
    setBusy(true);
    try {
      const res = await submit({ data: { id, answers } });
      toast.success(`وصلت الإجابات · النتيجة ${res.score.total} (${res.score.band ?? "—"})`);
      qc.invalidateQueries({ queryKey: ["me", "attempts"] });
      onClose();
    } catch (e: any) { toast.error(e?.message ?? "ما قدرنا نرسل الإجابات. منجرّب كمان مرة؟"); } finally { setBusy(false); }
  }

  const LIKERT = [
    { v: 0, l: "أبداً" }, { v: 1, l: "نادراً" }, { v: 2, l: "أحياناً" }, { v: 3, l: "غالباً" }, { v: 4, l: "دائماً" },
  ];

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[85vh] w-full max-w-2xl overflow-y-auto rounded-ds-xl bg-cream-50 p-6" dir="rtl">
        <DialogHeader>
          <DialogTitle className="mb-2 font-display text-xl font-extrabold text-start">{instrument?.title_ar ?? ""}</DialogTitle>
          {instrument?.description_ar ? (
            <DialogDescription className="mb-6 text-start text-[15px] text-brown-500">{instrument.description_ar}</DialogDescription>
          ) : null}
        </DialogHeader>
        {isLoading || !data ? (
          <div className="h-24 rounded-ds-lg bg-cream-100" aria-hidden />
        ) : (
          <>
        <div className="space-y-6">
          {sections.map((s: any) => (
            <div key={s.id}>
              <p className="mb-3 font-display text-[14px] font-bold text-amber-800">{s.title_ar}</p>
              <ul>
                {(s.questions ?? []).map((q: any) => (
                  <li key={q.id} className={rowCls}>
                    <p className="mb-3 text-[17px] text-brown-700">{q.text_ar}</p>
                    <div className="flex flex-wrap gap-2">
                      {LIKERT.map((opt) => (
                        <button key={opt.v} onClick={() => setAnswers((a) => ({ ...a, [q.id]: opt.v }))}
                          aria-pressed={answers[q.id] === opt.v}
                          className={`min-h-12 whitespace-nowrap rounded-ds-pill border px-3 font-display text-[13px] font-bold transition-colors duration-ds-fast ${answers[q.id] === opt.v ? "border-amber-600 bg-amber-100 text-brown-700" : "border-hairline-default bg-surface text-brown-700 hover:bg-cream-50"}`}>
                          {opt.l}
                        </button>
                      ))}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <button onClick={onClose} className={ghostPill}>إلغاء</button>
          <button onClick={finish} disabled={busy} className={primaryPill}>
            {busy ? "جاري الإرسال…" : "إرسال الإجابات"}
          </button>
        </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function MyBookings() {
  const { data: bookings, isLoading } = useQuery(myBookingsQuery());
  const { data: services } = useQuery(servicesQuery());
  const { data: siteSettings } = useQuery(publicSiteSettingsQuery());
  const supportWa = siteSettings?.clinic_whatsapp ? toWhatsAppLink(siteSettings.clinic_whatsapp, "مرحبا، عندي سؤال عن حجزي") : null;
  if (isLoading) return <div className="h-32 rounded-ds-xl bg-cream-100" aria-hidden />;
  if (!bookings || bookings.length === 0) {
    return (
      <EmptyState message="لسّا ما في حجوزات. أول استشارة بتبلّش من هون." cta={{ to: "/book", label: "حجز استشارة ←" }} />
    );
  }
  return (
    <ul className="space-y-3">
      {bookings.map((b: any) => (
        <BookingCard key={b.id} b={b} service={(services ?? []).find((s: any) => s.id === b.service_id) as any} whatsapp={supportWa} />
      ))}
    </ul>
  );
}


function Info({ label, value }: { label: string; value: string }) {
  return (
    <div className="border-b border-hairline-subtle py-3">
      <p className="text-[13px] font-bold text-brown-500">{label}</p>
      <p className="mt-1 font-display text-[17px] font-bold text-brown-700">{value}</p>
    </div>
  );
}